(function () {
  // Footer node refs are resolved LAZILY (not at a single init()) because the
  // deep-link auto-open paths in script-index-filters.js / script-index-product.js
  // fire on DOMContentLoaded BEFORE this section's script registers its own
  // listener — so __footerToModal must already exist (and find the footer) by the
  // time those run, or the footer is never moved into the modal and stays hidden
  // behind the display:none'd index sections. Defining the public API at IIFE
  // top-level (synchronously) guarantees it.
  var footer = null;
  var homeParent = null;
  var homeNextSibling = null;
  var applying = false;

  // --- PHONE: THIS SCRIPT DOES NOTHING -------------------------------------
  // Pinning is a measure-and-reflow cycle: the class comes off, the footer's
  // natural offsetTop is read (a forced synchronous layout), and the class goes
  // back on or not — which moves the footer in and out of the flow and changes
  // the document's height. On a desktop that is a rare, deliberate decision. On a
  // phone it is triggered constantly by things that have nothing to do with the
  // footer: every ResizeObserver callback from the body, every `resize` iOS fires
  // as the URL bar collapses during an ordinary scroll, every image that lands.
  // Each one is a chance for the page to change height under a scroll in
  // progress, and that is the layout shift at the bottom of a mobile page.
  //
  // There is nothing to gain from it here either — a phone page is taller than
  // the viewport in every case this was written for — so below the breakpoint the
  // footer is simply what it is in the markup: the last thing in the flow. No
  // measurement, no observer, no class.
  var MOBILE_Q = '(max-width: 573px)';
  var mq = typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_Q) : null;

  function isMobile() {
    return mq ? mq.matches : window.innerWidth <= 573;
  }

  // --- HARD COLLECTION PAGE: THIS SCRIPT DOES NOTHING EITHER ----------------
  // templates/collection.liquid seats the footer with CSS alone
  // (body.template-collection .footer-wrapper { margin-top:auto } in
  // style-index.css — the body is a min-height:100svh flex column). No pin, no
  // measurement, at any width.
  function isCollectionTemplate() {
    return !!(document.body && document.body.classList.contains('template-collection'));
  }

  // Widths / templates where the footer is left as plain flow.
  function isFlowOnly() {
    return isMobile() || isCollectionTemplate();
  }

  function ensureFooter() {
    if (footer && footer.isConnected) return footer;
    var found = document.querySelector('.footer-wrapper');
    if (found) {
      footer = found;
      // Capture the home slot the FIRST time we see the footer, before any move,
      // so __footerToHome can put it back exactly where index-footer rendered it.
      if (!homeParent) {
        homeParent = footer.parentNode;
        homeNextSibling = footer.nextSibling;
      }
    }
    return footer;
  }

  function update() {
    if (applying) return;
    var f = ensureFooter();
    if (!f) return;

    // Phone / hard collection page: never pinned, and never measured — see the
    // notes at the top. The class is cleared rather than left alone, so a window
    // dragged down past the breakpoint (or a rotation) cannot strand the footer
    // fixed to the viewport.
    if (isFlowOnly()) {
      f.classList.remove('footer-pinned');
      return;
    }
    // Detached mid-move (modalInner.innerHTML cleared on rebuild/close) — skip;
    // the re-attach that follows fires the observer and we re-measure then.
    if (!f.isConnected) return;

    // NOTE: do NOT arm `applying` here. It is armed ONLY in the state-flipped
    // branch below. Setting it unconditionally at the top latched the guard
    // forever on the FIRST unchanged decision (e.g. the initial pre-layout
    // measurement reads tall → shouldPin=false, equal to the default unpinned
    // class → "unchanged" → guard never released), which then swallowed every
    // later resize and left short pages stuck UNPINNED under the grid.

    // Measure the footer's NATURAL in-flow top = the real height of everything
    // above it. The footer packs directly after the content (no margin-top:auto),
    // so this is NOT inflated by the body's min-height:100svh the way
    // scrollHeight is. offsetTop is only meaningful in the UNPINNED state.
    var wasPinned = f.classList.contains('footer-pinned');
    if (wasPinned) f.classList.remove('footer-pinned');
    var contentAboveFooter = f.offsetTop; // forces a sync layout read
    var shouldPin = contentAboveFooter <= window.innerHeight;
    f.classList.toggle('footer-pinned', shouldPin);

    // Re-entry guard, but ONLY for the frame where we actually FLIPPED the pin
    // state. Pinning/unpinning moves the footer in/out of flow and changes body
    // height, which spawns a self-induced ResizeObserver callback we must
    // swallow (else infinite toggle loop). When the decision is UNCHANGED the
    // remove+re-add nets zero body-height change and spawns no RO callback — so
    // we leave the guard open. Closing it unconditionally (the old behavior)
    // also swallowed a *legit* co-occurring resize — e.g. the index sections
    // being restored to display in showSections(), or images finishing load —
    // which left the footer stuck pinned over a now-tall grid until the next
    // manual scroll. (Bugs P1/P2/P3.)
    if (shouldPin !== wasPinned) {
      applying = true;
      requestAnimationFrame(function () { applying = false; });
    }
  }

  // Public re-measure. Callers that restore content height OUT of band with the
  // ResizeObserver (footer re-homed after a collection modal closes, or the
  // collection grid re-shown after a PDP closes) invoke this so the pin state is
  // recomputed against the now-final layout instead of a stale measurement.
  window.__footerUpdate = update;

  // --- Collection modal hand-off -------------------------------------------
  // MOVE the one real footer into the modal as the last child of
  // #collection-modal-inner so it sits AFTER the collection grid in flow;
  // update() pins it ONLY when the collection is shorter than the viewport.
  window.__footerToModal = function (modalInner) {
    var f = ensureFooter();
    if (!f || !modalInner) return;
    modalInner.appendChild(f);
    update();
  };

  window.__footerToHome = function () {
    var f = ensureFooter();
    if (!f) return;
    // Put it back exactly where index-footer rendered it.
    if (homeNextSibling && homeNextSibling.parentNode === homeParent) {
      homeParent.insertBefore(f, homeNextSibling);
    } else if (homeParent) {
      homeParent.appendChild(f);
    }
    update();
  };

  // The body observer is the expensive half of this — it fires on every height
  // change the page makes — so on a phone it is not merely ignored, it is not
  // connected. Kept in step with the breakpoint so a desktop window narrowed past
  // it drops the observer, and a phone rotated into a wide layout gains one.
  var ro = null;

  function syncObserver() {
    if (isFlowOnly() || !('ResizeObserver' in window)) {
      if (ro) { ro.disconnect(); ro = null; }
      return;
    }
    if (!ro) {
      ro = new ResizeObserver(update);
      ro.observe(document.body);
    }
  }

  function init() {
    if (!ensureFooter()) return;
    update();

    // resize stays bound at every width: it is what notices the breakpoint being
    // crossed, and on a phone it costs one isMobile() test and a return.
    window.addEventListener('resize', function () {
      syncObserver();
      update();
    });
    window.addEventListener('load', update);

    if (mq) {
      var onChange = function () { syncObserver(); update(); };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }

    syncObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
